import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, AuthSeal } from './AuthLayout.js';
import { loginRequest } from './api.js';
import { useAuthStore } from '../store/auth.js';
import { AuthApiError } from './api.js';
import { OAuthProviders } from './OAuthProviders.js';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setUser = useAuthStore((s) => s.setUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await loginRequest({ email, password });
      setUser(user);
      // P23 Batch F — default landing target after login is `/play` (the
      // game), not `/` (which is the public marketing page). The
      // `?next=` query param still wins, so deep-linked redirects from
      // RequireAuth round-trip correctly.
      const next = params.get('next') ?? '/play';
      navigate(decodeURIComponent(next), { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(
          err.code === 'RATE_LIMITED'
            ? 'Too many attempts. Wait a minute.'
            : 'Email or password is incorrect.',
        );
      } else {
        setError('Network error — check your connection.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <div className="overflow-hidden rounded-lg border border-t-[3px] border-line border-t-accent bg-panel shadow-soft">
        {/* header */}
        <div className="flex items-center gap-4 border-b border-line px-6 py-5">
          <AuthSeal />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
              Stellar Freight Co. · CTA Authentication
            </p>
            <h1 className="mt-1 text-[22px] font-extrabold leading-tight tracking-tight text-fg">
              Login to Charter
            </h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 px-6 py-6">
          <Field label="Email address">
            <Input
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="corp@example.com"
              autoComplete="email"
              required
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </Field>

          {error && <FormError message={error} />}

          <SubmitButton busy={busy} label="Login" busyLabel="Authenticating…" />
        </form>

        <OAuthProviders />

        <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-t border-line px-6 py-4 text-[11px] font-semibold">
          <Link
            to="/signup"
            className="text-accent transition-colors hover:underline"
          >
            Register new charter →
          </Link>
          <Link
            to="/reset-password"
            className="text-fg-2 transition-colors hover:text-fg"
          >
            Forgot password
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

// ── Shared form primitives (auth pages only) ────────────────────────────────

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2">
        {label}
      </span>
      {children}
    </label>
  );
}

export function Input({
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
}: {
  type: 'email' | 'password' | 'text';
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      className="w-full rounded-md border border-line bg-panel-2 px-3 py-2.5 font-mono text-[13px] text-fg outline-none transition-colors placeholder:text-muted/70 hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/30"
    />
  );
}

export function FormError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-bad/40 bg-bad/10 px-3 py-2 text-[12px] font-semibold text-bad"
    >
      {message}
    </div>
  );
}

export function SubmitButton({
  busy,
  label,
  busyLabel,
}: {
  busy: boolean;
  label: string;
  busyLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full rounded-md bg-accent px-4 py-2.5 text-[13px] font-bold tracking-wide text-white transition-all hover:brightness-105 disabled:cursor-wait disabled:opacity-60"
    >
      {busy ? busyLabel : label}
    </button>
  );
}
